import React, { useEffect, useMemo, useState } from "react";
import useFetch from "hooks/useFetch";
import {
  Badge,
  BadgeTone,
  Button,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  styles as ui,
  Tabs,
  TextField,
  useConfirm,
  useToast,
} from "stories/Admin";
import styles from "./AiBonFeedbackPage.module.scss";

interface AiBonFeedback {
  id: number;
  userUid: string | null;
  question: string | null;
  answer: string;
  reason: string | null;
  comment: string | null;
  mosqueOrgNr: string | null;
  locationId: number | null;
  platform: string | null;
  status: string;
  createdAt: string;
}

interface AiBonChat {
  id: number;
  userUid: string | null;
  question: string;
  answer: string;
  modelUsed: string | null;
  createdAt: string;
}

interface AiBonChatPage {
  content: AiBonChat[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

type ActiveTab = "feedback" | "chats";

const STATUS_OPTIONS = ["NEW", "IN_PROGRESS", "RESOLVED"] as const;

const statusLabel = (status: string): string => {
  switch (status) {
    case "NEW":
      return "Ny";
    case "IN_PROGRESS":
      return "Under arbeid";
    case "RESOLVED":
      return "Løst";
    default:
      return status;
  }
};

const statusTone = (status: string): BadgeTone => {
  switch (status) {
    case "NEW":
      return "rose";
    case "IN_PROGRESS":
      return "gold";
    case "RESOLVED":
      return "mint";
    default:
      return "grey";
  }
};

const truncate = (text: string, max = 280): string => {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
};

const AiBonFeedbackPage: React.FC = () => {
  const { execute } = useFetch();
  const confirm = useConfirm();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<ActiveTab>("feedback");

  // Feedback state
  const [items, setItems] = useState<AiBonFeedback[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const itemsPerPage = 20;

  // Chat dump state
  const [chats, setChats] = useState<AiBonChat[]>([]);
  const [chatSearch, setChatSearch] = useState("");
  const [chatSearchApplied, setChatSearchApplied] = useState("");
  const [chatPage, setChatPage] = useState(0);
  const [chatTotalPages, setChatTotalPages] = useState(1);
  const [chatTotal, setChatTotal] = useState(0);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [expandedChatId, setExpandedChatId] = useState<number | null>(null);
  const chatPageSize = 20;

  const fetchAll = async () => {
    setIsLoading(true);
    const result = (await execute(
      "/api/v1/admin/ai-prayer-times/feedback",
      "GET",
    )) as AiBonFeedback[] | null;
    if (result) setItems(result);
    setIsLoading(false);
  };

  const fetchChats = async (page = 0, q = chatSearchApplied) => {
    setIsLoadingChats(true);
    const result = (await execute(
      "/api/v1/admin/ai-prayer-times/chats",
      "GET",
      undefined,
      {
        page,
        size: chatPageSize,
        ...(q.trim() ? { q: q.trim() } : {}),
      },
    )) as AiBonChatPage | null;
    if (result) {
      setChats(result.content ?? []);
      setChatPage(result.page ?? 0);
      setChatTotalPages(Math.max(1, result.totalPages ?? 1));
      setChatTotal(result.totalElements ?? 0);
    }
    setIsLoadingChats(false);
  };

  useEffect(() => {
    if (activeTab === "feedback") {
      fetchAll();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "chats") {
      fetchChats(0, chatSearchApplied);
    }
  }, [activeTab, chatSearchApplied]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return items.filter((it) => {
      const matchesStatus = !statusFilter || it.status === statusFilter;
      if (!q) return matchesStatus;
      const haystack = `${it.question ?? ""} ${it.answer} ${it.comment ?? ""} ${
        it.reason ?? ""
      } ${it.mosqueOrgNr ?? ""}`.toLowerCase();
      return matchesStatus && haystack.includes(q);
    });
  }, [items, searchTerm, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filtered.length]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirst, indexOfLast);

  const newCount = useMemo(
    () => items.filter((it) => it.status === "NEW").length,
    [items],
  );

  const handleStatusChange = async (item: AiBonFeedback, status: string) => {
    const updated = (await execute(
      `/api/v1/admin/ai-prayer-times/feedback/${item.id}/status`,
      "PATCH",
      { status },
    )) as AiBonFeedback | null;
    if (updated) {
      setItems((prev) => prev.map((x) => (x.id === item.id ? updated : x)));
    }
  };

  const handleDeleteClick = async (selected: AiBonFeedback) => {
    const ok = await confirm({
      title: "Slette tilbakemeldingen?",
      message: "Tilbakemeldingen slettes for godt. Dette kan ikke angres.",
      confirmLabel: "Slett",
      tone: "danger",
    });
    if (!ok) return;
    await execute(
      `/api/v1/admin/ai-prayer-times/feedback/${selected.id}`,
      "DELETE",
    );
    setItems((prev) => prev.filter((x) => x.id !== selected.id));
    toast.success("Tilbakemeldingen er slettet.");
  };

  const handleChatSearch = () => {
    setChatPage(0);
    setChatSearchApplied(chatSearch.trim());
  };

  const refresh = () =>
    activeTab === "feedback"
      ? fetchAll()
      : fetchChats(chatPage, chatSearchApplied);

  const loadingCards = (
    <>
      {[0, 1].map((i) => (
        <Card key={i}>
          <Skeleton width="30%" height={22} />
          <Skeleton height={44} />
          <Skeleton width="70%" />
        </Card>
      ))}
    </>
  );

  return (
    <>
      <PageHeader
        title={
          <>
            Tilbakemeldinger{" "}
            {activeTab === "feedback" && newCount > 0 && (
              <Badge tone="rose" className={styles.titleBadge}>
                {newCount} nye
              </Badge>
            )}
          </>
        }
        subtitle={
          activeTab === "feedback"
            ? "Rapporter fra brukere som mener et AiBønn-svar er feil eller dårlig."
            : "Alle AiBønn-spørsmål og -svar fra databasen, nyeste først."
        }
        actions={
          <Button
            variant="secondary"
            onClick={refresh}
            loading={activeTab === "feedback" ? isLoading : isLoadingChats}
            icon={<Icon name="swap" size={16} strokeWidth={2} />}
          >
            Oppdater
          </Button>
        }
      />

      <Tabs
        label="Tilbakemeldinger"
        value={activeTab}
        onChange={setActiveTab}
        tabs={[
          { value: "feedback", label: "Tilbakemeldinger" },
          { value: "chats", label: "Søk i alle chatter" },
        ]}
      />

      {activeTab === "feedback" && (
        <>
          <div className={styles.filters}>
            <div className={styles.search}>
              <Icon
                name="search"
                size={18}
                strokeWidth={2}
                className={styles.searchIcon}
              />
              <TextField
                type="search"
                aria-label="Søk i tilbakemeldinger"
                placeholder="Søk i spørsmål, svar, kommentar, årsak eller org.nr."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select
              aria-label="Status"
              fieldClassName={styles.statusFilter}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Alle statuser</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s)}
                </option>
              ))}
            </Select>
            <span className={ui.help}>
              Viser {filtered.length} av {items.length}
            </span>
          </div>

          {isLoading && items.length === 0 ? (
            loadingCards
          ) : filtered.length === 0 ? (
            <EmptyState icon="thumbs" title="Ingen tilbakemeldinger">
              {items.length > 0
                ? "Ingen tilbakemeldinger passer søket eller statusen."
                : "Det har ikke kommet noen tilbakemeldinger ennå."}
            </EmptyState>
          ) : (
            currentItems.map((it) => (
              <Card key={it.id} className={styles.item}>
                <div className={styles.badges}>
                  <Badge tone={statusTone(it.status)}>
                    {statusLabel(it.status)}
                  </Badge>
                  {it.platform && <Badge tone="grey">{it.platform}</Badge>}
                  {it.reason && <Badge tone="lilac">{it.reason}</Badge>}
                  <span className={styles.date}>
                    {new Date(it.createdAt).toLocaleString("no-NO")}
                  </span>
                </div>
                <div className={ui.g2}>
                  <div>
                    <span className={ui.lbl}>Spørsmål</span>
                    <p className={styles.text}>{it.question || "—"}</p>
                  </div>
                  <div>
                    <span className={ui.lbl}>Svar</span>
                    <p className={`${styles.text} ${styles.answer}`}>
                      {it.answer}
                    </p>
                  </div>
                </div>
                {it.comment && (
                  <div className={styles.comment}>
                    <b>Kommentar fra bruker:</b> {it.comment}
                  </div>
                )}
                <div className={styles.footer}>
                  <span className={ui.help}>
                    {it.mosqueOrgNr && <>Moské: {it.mosqueOrgNr} · </>}
                    {it.locationId != null && <>Lokasjon: {it.locationId} · </>}
                    {it.userUid ? <>Bruker: {it.userUid}</> : <>Anonym</>}
                  </span>
                  <span className={styles.footerActions}>
                    <Select
                      aria-label="Endre status"
                      fieldClassName={styles.statusSelect}
                      className={styles.statusSelectInput}
                      value={it.status}
                      onChange={(e) => handleStatusChange(it, e.target.value)}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {statusLabel(s)}
                        </option>
                      ))}
                    </Select>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteClick(it)}
                      icon={<Icon name="trash" size={14} strokeWidth={2} />}
                    >
                      Slett
                    </Button>
                  </span>
                </div>
              </Card>
            ))
          )}

          <Pagination
            page={currentPage}
            pageCount={totalPages}
            onChange={setCurrentPage}
          />
        </>
      )}

      {activeTab === "chats" && (
        <>
          <form
            className={styles.filters}
            onSubmit={(e) => {
              e.preventDefault();
              handleChatSearch();
            }}
          >
            <div className={styles.search}>
              <Icon
                name="search"
                size={18}
                strokeWidth={2}
                className={styles.searchIcon}
              />
              <TextField
                type="search"
                aria-label="Søk i alle chatter"
                placeholder="Søk i spørsmål, svar, bruker eller modell"
                value={chatSearch}
                onChange={(e) => setChatSearch(e.target.value)}
              />
            </div>
            <Button type="submit">Søk</Button>
            {chatSearchApplied && (
              <Button
                variant="secondary"
                onClick={() => {
                  setChatSearch("");
                  setChatSearchApplied("");
                }}
              >
                Nullstill
              </Button>
            )}
          </form>
          <span className={ui.help}>
            Viser side {chatPage + 1} av {chatTotalPages} · {chatTotal} totalt
            {chatSearchApplied ? ` · filter: «${chatSearchApplied}»` : ""}
          </span>

          {isLoadingChats && chats.length === 0 ? (
            loadingCards
          ) : chats.length === 0 ? (
            <EmptyState icon="search" title="Ingen chatter">
              Fant ingen chatter.
            </EmptyState>
          ) : (
            chats.map((chat) => {
              const expanded = expandedChatId === chat.id;
              return (
                <Card key={chat.id} className={styles.item}>
                  <div className={styles.badges}>
                    <Badge tone="grey">#{chat.id}</Badge>
                    {chat.modelUsed && (
                      <Badge tone="blue">{chat.modelUsed}</Badge>
                    )}
                    <Badge tone="lilac">
                      {chat.userUid ? chat.userUid : "Anonym"}
                    </Badge>
                    <span className={styles.date}>
                      {new Date(chat.createdAt).toLocaleString("no-NO")}
                    </span>
                  </div>
                  <div>
                    <span className={ui.lbl}>Spørsmål</span>
                    <p className={styles.text}>{chat.question}</p>
                  </div>
                  <div>
                    <span className={ui.lbl}>Svar</span>
                    <p className={`${styles.text} ${styles.answer}`}>
                      {expanded ? chat.answer : truncate(chat.answer)}
                    </p>
                    {chat.answer.trim().length > 280 && (
                      <button
                        type="button"
                        className={`${ui.linkBtn} ${styles.more}`}
                        onClick={() =>
                          setExpandedChatId(expanded ? null : chat.id)
                        }
                      >
                        {expanded ? "Vis mindre" : "Vis hele svaret"}
                      </button>
                    )}
                  </div>
                </Card>
              );
            })
          )}

          <Pagination
            page={chatPage + 1}
            pageCount={chatTotalPages}
            onChange={(p) => {
              const next = p - 1;
              setChatPage(next);
              fetchChats(next, chatSearchApplied);
            }}
          />
        </>
      )}
    </>
  );
};

export default AiBonFeedbackPage;
